import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import axios from "axios";

import api from "libs/api";
import notificationSound from "assets/sounds/notification.mp3";

import { useApp } from "context/app";

let audio = new Audio(notificationSound);

const TaskViewContext = createContext();

const TaskViewProvider = ({ children }) => {
  const { t } = useTranslation();
  const { user, socket } = useApp();

  const { id } = useParams();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [is_message_sending, setIsMessageSending] = useState(false);
  const [file_progress, setFileProgress] = useState(null);

  useEffect(() => {
    (async function () {
      await getTaskViewData();
    })();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (!!!socket) return;

    socket.emit("join_task", id);
    socket.on("task_update", (task) => {
      const last_user_id =
        task?.messages?.[task?.messages?.length - 1]?.user?._id;

      setTask((prev) => ({ ...prev, ...task }));

      if (user?._id !== last_user_id) audio.play();
    });

    return () => {
      if (!!!socket) return;

      socket.emit("task_leave", id);
    };

    // eslint-disable-next-line
  }, [socket]);

  const getTaskViewData = async (refetch) => {
    try {
      if (!!!refetch) {
        setLoading(true);
      }
      const task = await api.get(`/admin/tasks/${id}/conversation`);
      setTask(task);
      setLoading(false);
    } catch (err) {
      toast.error(t("Task not found."));
      setError(true);
    }
  };

  const sendMessage = async (text, files) => {
    setIsMessageSending(true);

    const formData = new FormData();
    formData.append("text", text);
    files?.forEach((item) => {
      formData.append("files", item);
    });

    try {
      await axios.post(
        `${process.env.REACT_APP_API}/admin/tasks/${id}/send-message`,
        formData,
        {
          withCredentials: true,
          onUploadProgress: ({ loaded, total }) => {
            const percent = Math.floor((loaded * 100) / total);
            setFileProgress(percent);
          },
        },
      );
    } catch (err) {
      console.log(err);
    }

    setTimeout(() => {
      setIsMessageSending(false);
      setFileProgress(null);
    }, 200);
  };

  const editMessage = async (
    message_id,
    text,
    files,
    files_to_remove,
    is_optimistic,
  ) => {
    const task_fallback = task;
    const hasFiles = files?.length > 0 || files_to_remove?.length > 0;

    if (hasFiles) setFileProgress(0);

    try {
      const formData = new FormData();
      if (text !== "KEEP_SAME") {
        formData.append("text", text);

        if (is_optimistic) {
          setTask((prevTask) => {
            return {
              ...prevTask,
              messages: prevTask?.messages?.map((item) => {
                if (item._id === message_id) {
                  const currentFiles = item?.files ?? [];

                  return {
                    ...item,
                    text,
                    files: hasFiles
                      ? [
                          ...currentFiles.filter(
                            (file) => !files_to_remove.includes(file?.key),
                          ),
                          ...files,
                        ]
                      : item?.files,
                    updated_at: new Date().toISOString(),
                  };
                }
                return item;
              }),
            };
          });
        }
      }
      files_to_remove?.forEach((item) => {
        formData.append("files_to_remove", item);
      });
      files?.forEach((item) => {
        formData.append("files", item);
      });

      await axios.put(
        `${process.env.REACT_APP_API}/admin/tasks/${id}/edit-message/${message_id}`,
        formData,
        {
          withCredentials: true,
          onUploadProgress: ({ loaded, total }) => {
            if (hasFiles) {
              const percent = Math.floor((loaded * 100) / total);
              setFileProgress(percent);
            }
          },
        },
      );

      getTaskViewData(true);
    } catch (err) {
      if (is_optimistic) setTask(task_fallback);
      console.error(err);
    }

    setTimeout(() => {
      setFileProgress(null);
    }, 200);
  };

  const optimisticUpdate = (values) => {
    setTask((prev) => ({ ...prev, ...values }));
  };

  const value = useMemo(() => {
    return {
      data: task,
      refetch: async (value = true) => await getTaskViewData(value),
      optimisticUpdate,
      file_progress,
      is_message_sending,
      sendMessage,
      editMessage,
      loading,
      error,
    };
    // eslint-disable-next-line
  }, [task, file_progress, loading, error]);

  return (
    <TaskViewContext.Provider value={value}>
      {children}
    </TaskViewContext.Provider>
  );
};

const useTaskView = () => useContext(TaskViewContext);
export { TaskViewContext, useTaskView };
export default TaskViewProvider;
